<script setup lang="ts">
  import axios from 'axios';
  import { ref, computed, getCurrentInstance, watch } from 'vue';
  import { Globals } from '@/modules/Globals';
  import { vueData } from '@/VueData';
  import { PaymentMethod } from '@/enums/PaymentMethod';
  import { IOrder } from '@/interfaces/IOrder';
  import { isNumeric, round2, roundPrice } from '@/utils';
  import OrdersService from '@/api/services/orders.service';
  import { OrderPaymentStatus } from '@/enums/OrderEnums';
  import { AlertType } from '@/interfaces/IAlert';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import ModalsCustomers from '@/components/modals/customer/ModalsCustomers.vue';
  import { ICustomer } from '@/interfaces/ICustomer';
  import { IClubTenderMember } from '@/interfaces/IClubMember';
  import {
    ClubService,
    isClubCodeRequiredError,
  } from '@/api/services/club.service';
  import { useModal } from '@/composition/use-modal';
  import { useSocketStore } from '@/composition/use-socket';
  import { useUi } from '@/composition/use-ui';
  import { PaymentType } from '@/enums/PaymentEnums';
  import PaymentsService from '@/api/services/payments.service';
  import { useAuthStore } from '@/composition/use-auth';
  import { PaymentEntityType } from '@/enums/PaymentEntityType';
  import { useOrders } from '@/composition/use-orders';
  import { getEffectiveTier } from '@/utils/billing-entitlements.utils';
  import { CHARGEABLE_PAYMENT_METHODS } from '@/domain/payments/payment-method-lists';
  import {
    CLUB_CODE_HINT_KEYS,
    TClubCodeHint,
  } from '@/domain/club/club-verification';

  /**
   * "Usar saldo" presets (POS V2 member panel): open on Saldo Club with the
   * member already identified. The member card skips the code step only
   * while the cashier's identification grant is live (`presetClubVerified`).
   */
  export interface IModalPayPresetProps {
    presetMethod?: PaymentMethod;
    presetClubMember?: IClubTenderMember | null;
    presetClubVerified?: boolean;
  }

  const props = defineProps<{
    type: PaymentType;
    unpaidAmount: number;
    entityIds: number[];
    title?: string;
    hiddenMethods?: PaymentMethod[];
    presetMethod?: PaymentMethod;
    presetClubMember?: IClubTenderMember | null;
    presetClubVerified?: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'close'): void;
  }>();

  const { proxy } = getCurrentInstance();

  const restaurantStore = useRestaurantStore();
  const modal = useModal();
  const ui = useUi();
  const authStore = useAuthStore();
  const ordersHelper = useOrders();

  const unpaidAmount = computed(() => roundPrice(props.unpaidAmount));

  const totalToPay = ref(unpaidAmount.value || 0);
  const totalPaid = ref(0);

  // Accumulates every method charged in this modal session (survives
  // resetPaymentData). Emitted as the third `paid` arg so auto-invoice gates
  // do not race on WS-refreshed paymentsByType.
  const sessionPaymentMethods: PaymentMethod[] = [];

  const remainingToPay = computed(() =>
    round2(totalToPay.value - totalPaid.value)
  );

  // Club stored value is a real tender only when the restaurant enabled it;
  // call sites that must never charge balance (tips, customer/club top-ups)
  // additionally pass it in hiddenMethods.
  const clubBalanceEnabled = computed(
    () => !!restaurantStore.restaurant?.club?.balanceEnabled
  );

  const paymentMethods = computed(() =>
    Globals.shortPaymentMethods.filter(
      (method) =>
        !props.hiddenMethods?.includes?.(method.id) &&
        (method.id !== PaymentMethod.Club || clubBalanceEnabled.value) &&
        CHARGEABLE_PAYMENT_METHODS.includes(method.id)
    )
  );

  const paymentData = ref({
    amount: (unpaidAmount.value > 0 ? unpaidAmount.value + '' : '') as
      | string
      | null,
    method: null as PaymentMethod | null,
    comment: null as string | null,
    customerId: null as number | null,
  });

  const selectedCustomer = ref<ICustomer | null>(null);
  const paying = ref(false);

  //#region Club balance tender

  const socket = useSocketStore();

  const clubMember = ref<IClubTenderMember | null>(null);
  const clubCodeInput = ref('');
  const clubResolving = ref(false);
  const clubCodeError = ref<string | null>(null);
  // The resolved code is kept and sent with the payment: the server consumes
  // it there (single-use).
  let resolvedClubCode = '';
  let clubResolveSeq = 0;
  // Member identified through the cashier's live grant: no code is sent.
  const useClubGrant = ref(false);
  // Whether the preset member can be charged code-less right now. Starts
  // from the grant state the opener saw; a 428 turns it off, a code-based
  // payment of the same member turns it on (the server then issued a grant
  // bound to this order).
  const presetVerified = ref(!!props.presetClubVerified);
  // The server answered 428: the grant no longer covers this order.
  const presetInvalidated = ref(false);
  // Wallet charged in this modal session: the preset is a snapshot taken
  // when the modal opened, so a second Saldo Club leg must see the wallet
  // minus what the first leg already took.
  let clubSessionSpent = 0;
  // Wallet-capped "everything" written by the club tender: leaving Saldo
  // Club with it untouched restores the full remaining amount, so a cash
  // payment does not silently become a partial one.
  let clubAutoAmount: string | null = null;

  /** Member card from the preset (grant path), with the amount prefilled. */
  function applyClubPreset() {
    if (
      paymentData.value.method !== PaymentMethod.Club ||
      !props.presetClubMember ||
      !presetVerified.value
    ) {
      return;
    }
    clubMember.value = {
      ...props.presetClubMember,
      cashTotal: roundPrice(
        Math.max(props.presetClubMember.cashTotal - clubSessionSpent, 0)
      ),
    };
    resolvedClubCode = '';
    useClubGrant.value = true;
    capDefaultAmountToWallet();
  }

  /**
   * Cap the default "everything" amount to the member's wallet; an amount
   * the cashier typed themselves is theirs to keep.
   */
  function capDefaultAmountToWallet() {
    if (!clubMember.value) return;
    const typed = paymentData.value.amount;
    if (
      !isNumeric(typed) ||
      roundPrice(parseFloat(typed!)) === remainingToPay.value
    ) {
      clubAutoAmount = String(clubDefaultAmount.value);
      paymentData.value.amount = clubAutoAmount;
    }
  }

  // Preset member without a usable grant: tell the cashier to ask for a
  // code, and why (server rejected the grant vs. none covered the order).
  const clubCodeHint = computed<TClubCodeHint | null>(() => {
    if (!props.presetClubMember || clubMember.value) return null;
    if (presetInvalidated.value) return 'expired';
    return presetVerified.value ? null : 'needed';
  });
  const clubCodeHintText = computed(() =>
    clubCodeHint.value
      ? String(proxy.$t(CLUB_CODE_HINT_KEYS[clubCodeHint.value]))
      : null
  );

  function onClubCodeInput(value: string) {
    clubCodeError.value = null;
    clubCodeInput.value = value.replace(/\D+/g, '').slice(0, 6);
    if (clubCodeInput.value.length === 6) void resolveClubCode();
  }

  async function resolveClubCode() {
    if (clubCodeInput.value.length !== 6 || clubResolving.value) return;

    const seq = ++clubResolveSeq;
    clubResolving.value = true;
    try {
      const member = await ClubService.identify(
        restaurant.value.id,
        clubCodeInput.value
      );
      if (seq !== clubResolveSeq) return;
      resolvedClubCode = clubCodeInput.value;
      useClubGrant.value = false;
      clubMember.value = member;
      capDefaultAmountToWallet();
    } catch (e) {
      if (seq !== clubResolveSeq) return;
      if (axios.isAxiosError(e) && e.response?.status === 404) {
        clubCodeError.value = String(proxy.$t('posV2.clubInvalidCode'));
        clubCodeInput.value = '';
      } else {
        ui.serverError(e, String(proxy.$t('posV2.clubResolveError')));
      }
    } finally {
      if (seq === clubResolveSeq) clubResolving.value = false;
    }
  }

  /** A failed club payment may have burnt the code: force a fresh one. */
  function resetClubState() {
    // Invalidate any in-flight resolve so a late response cannot resurrect
    // the cleared member card.
    clubResolveSeq++;
    clubResolving.value = false;
    clubMember.value = null;
    clubCodeInput.value = '';
    clubCodeError.value = null;
    resolvedClubCode = '';
    useClubGrant.value = false;
  }

  const clubAmountExceedsBalance = computed(() => {
    if (paymentData.value.method !== PaymentMethod.Club) return false;
    if (!clubMember.value || !paymentAmountChosen.value) return false;
    return (
      roundPrice(parseFloat(paymentData.value.amount!)) >
      roundPrice(clubMember.value.cashTotal)
    );
  });

  /** What "Todo" means for a balance tender: the wallet caps the amount. */
  const clubDefaultAmount = computed(() =>
    roundPrice(
      Math.min(
        remainingToPay.value,
        clubMember.value?.cashTotal ?? remainingToPay.value
      )
    )
  );

  /** "Queda por pagar / Saldo después" once a valid club amount is typed. */
  const clubPreview = computed(() => {
    if (paymentData.value.method !== PaymentMethod.Club) return null;
    if (!clubMember.value || !paymentAmountChosen.value) return null;
    if (clubAmountExceedsBalance.value) return null;
    const amount = roundPrice(parseFloat(paymentData.value.amount!));
    return {
      remaining: roundPrice(Math.max(remainingToPay.value - amount, 0)),
      balanceAfter: roundPrice(clubMember.value.cashTotal - amount),
    };
  });

  function setFullAmount() {
    const full =
      paymentData.value.method === PaymentMethod.Club && clubMember.value
        ? clubDefaultAmount.value
        : remainingToPay.value;
    paymentData.value.amount = String(full);
  }

  // Re-selecting Saldo Club after a partial payment reuses the live grant;
  // with the member card still up (tender switched and back) only the
  // default amount is re-capped.
  watch(
    () => paymentData.value.method,
    (method, previous) => {
      if (method === PaymentMethod.Club) {
        if (clubMember.value) capDefaultAmountToWallet();
        else applyClubPreset();
      }
      if (previous === PaymentMethod.Club && clubAutoAmount !== null) {
        if (paymentData.value.amount === clubAutoAmount) {
          paymentData.value.amount = String(remainingToPay.value);
        }
        clubAutoAmount = null;
      }
    }
  );

  if (props.presetMethod) {
    paymentData.value.method = props.presetMethod;
    applyClubPreset();
  }

  //#endregion

  /*const unpaidAmount = computed(() => {
    if (props.order) return props.order.total - props.order.totalPaid;
    else if (props.orders)
      return props.orders.reduce(
        (acc, order) => acc + (order.total - order.totalPaid),
        0
      );
    else throw new Error('No order or orders prop');
  });*/

  const currency = computed(() => vueData.currency);
  const restaurant = computed(() => vueData.restaurant);
  const branch = computed(() => vueData.branch);
  const caja = computed(() => restaurantStore.caja);

  const paymentAmountChosen = computed(() => {
    return (
      isNumeric(paymentData.value.amount) &&
      parseFloat(paymentData.value.amount) > 0
    );
  });

  const canPay = computed(() => {
    if (!paymentAmountChosen.value || !paymentData.value.method) return false;
    if (
      paymentData.value.method === PaymentMethod.CustomerAccount &&
      !paymentData.value.customerId
    )
      return false;
    if (paymentData.value.method === PaymentMethod.Club) {
      if (!clubMember.value || clubAmountExceedsBalance.value) return false;
    }
    return true;
  });

  function openCustomerSelector() {
    modal.show(
      ModalsCustomers as any,
      {},
      {
        name: 'customers',
        adaptive: true,
        classes: 'dialog--height-limit',
        height: 'auto',
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
        width: 600,
        clickToClose: false,
      },
      {
        chooseCustomer: (customer: ICustomer) => {
          selectedCustomer.value = customer;
          paymentData.value.customerId = customer.id;
          modal.hide('customers');
        },
      }
    );
  }

  async function doPayment() {
    if (!canPay.value) return;

    if (
      getEffectiveTier(restaurant.value) > 0 &&
      branch.value.settingUseCajas &&
      !caja.value
    ) {
      ui.showToast(
        AlertType.Error,
        'Debe seleccionar una caja para procesar pagos. Puede hacerlo en la parte superior de la pantalla. Si no creó ninguna Caja manual, créela primero desde Tienda -> Cajas'
      );
      return;
    }

    try {
      paying.value = true;

      let entityType: PaymentEntityType;
      switch (props.type) {
        case PaymentType.Order:
          entityType = PaymentEntityType.Order;
          break;
        case PaymentType.CustomerBalance:
          entityType = PaymentEntityType.Customer;
          break;
        case PaymentType.Tip:
          entityType = PaymentEntityType.Order;
          break;
        default:
          throw new Error(`Unsupported payment type: ${props.type}`);
      }

      // Snapshot before await: do not re-read after v2Create (offline fallback
      // response shape differs; canPay guarantees method is set).
      const method = paymentData.value.method!;

      // Club balance needs the live server (code consumption + balance
      // check): it must never fall into the offline payment queue.
      if (method === PaymentMethod.Club && !socket.isConnected) {
        ui.showToast(
          AlertType.Error,
          String(proxy.$t('posV2.clubNeedsConnection'))
        );
        return;
      }

      const payment = await PaymentsService.v2Create({
        platform: null,
        userId: authStore.user!.id,
        restaurantId: restaurant.value.id,
        branchId: branch.value.id,
        total: parseFloat(paymentData.value.amount!),
        comment: paymentData.value.comment,
        type: props.type,
        entityType,
        entityIds: props.entityIds,
        paymentMethod: method,
        cajaId: caja.value?.id,
        customerId: paymentData.value.customerId,
        clubMemberId:
          method === PaymentMethod.Club ? clubMember.value!.id : null,
        clubCode:
          method === PaymentMethod.Club && !useClubGrant.value
            ? resolvedClubCode
            : null,
      });

      sessionPaymentMethods.push(method);
      if (
        method === PaymentMethod.Club &&
        clubMember.value?.id === props.presetClubMember?.id
      ) {
        clubSessionSpent = roundPrice(clubSessionSpent + payment.total);
        // A code-based leg consumed a code and left a grant bound to this
        // order (single-order payments only): the next Saldo Club leg can
        // skip the code again.
        if (!useClubGrant.value && props.entityIds.length === 1) {
          presetVerified.value = true;
          presetInvalidated.value = false;
        }
      }

      totalPaid.value = roundPrice(totalPaid.value + payment.total);

      proxy.$parent.$emit('paid', payment.total, remainingToPay.value, [
        ...sessionPaymentMethods,
      ]);

      if (totalPaid.value < unpaidAmount.value) {
        ui.showToast(AlertType.Info, 'Pago registrado, falta por pagar');
        resetPaymentData();
      } else {
        ui.showToast(AlertType.Success, 'Pago completado');
        emit('close');
      }
    } catch (e) {
      console.error(e);

      if (isClubCodeRequiredError(e)) {
        // The grant no longer covers this order: fall back to the code step
        // with the expired hint instead of a generic error toast.
        presetVerified.value = false;
        presetInvalidated.value = true;
        resetClubState();
      } else {
        ui.serverError(e);

        // A rejected code-based club payment may have consumed the code
        // server-side: clear the member so the cashier asks for a fresh one.
        // The grant path sent no code, so the member card stays for a retry.
        if (
          paymentData.value.method === PaymentMethod.Club &&
          !useClubGrant.value
        ) {
          resetClubState();
        }
      }

      // Refresh the orders in case they were updated serverside
      if (props.type === PaymentType.Order && props.entityIds.length > 0) {
        // Try to get the updated order from the server
        for (const orderId of props.entityIds) {
          // Call refreshOrder with just the IDs instead of fetching the full order first
          ordersHelper
            .refreshOrder({
              orderId,
              restaurantId: restaurant.value.id,
            })
            .catch((err) => {
              console.error(
                `Error refreshing order ${orderId} after payment error`,
                err
              );
            });
        }
      }
    } finally {
      paying.value = false;
    }
  }

  function resetPaymentData() {
    paymentData.value.amount = null;
    paymentData.value.method = null;
    paymentData.value.comment = null;
    paymentData.value.customerId = null;
    selectedCustomer.value = null;
    // Codes are single-use: a settled club payment consumed this one, so the
    // next split needs a fresh resolve anyway.
    resetClubState();
  }
</script>

<template>
  <div class="order-pay-modal">
    <div class="amount-container">
      <h3>{{ props.title || 'Introduzca monto a pagar' }}</h3>
      <span v-if="unpaidAmount > 0" class="unpaid-amount">
        Restan pagar <strong>{{ currency.symbol }}{{ remainingToPay }}</strong>
      </span>
      <span v-else-if="props.type === PaymentType.Tip" class="unpaid-amount">
        Ingrese el monto de la propina
      </span>
      <v-form @submit.prevent>
        <div class="tw-flex tw-space-x-2">
          <v-text-field
            v-model="paymentData.amount"
            outlined
            label="Monto a pagar"
            type="number"
            :disabled="paying"
            hide-details
          />
          <v-btn
            @click="setFullAmount"
            color="primary"
            x-large
            :disabled="paying"
          >
            Todo
          </v-btn>
        </div>
      </v-form>
    </div>
    <v-divider />
    <div class="payment-method-container" :class="{ disabled: paying }">
      <h3>Seleccione método de pago</h3>
      <div class="payment-methods">
        <div
          class="payment-method"
          v-for="method in paymentMethods"
          :key="method.id"
          @click="paymentData.method = method.id"
          :class="{ selected: paymentData.method === method.id }"
        >
          <span>{{ method.title }}</span>
        </div>
      </div>
    </div>
    <template v-if="paymentData.method === PaymentMethod.Club">
      <v-divider />
      <div class="club-container">
        <template v-if="clubMember">
          <div class="club-member-card">
            <div class="club-member-info">
              <span class="club-member-name">{{ clubMember.name }}</span>
              <span class="club-member-number">
                {{
                  $t('posV2.clubMemberNumber', {
                    number: clubMember.memberNumber,
                  })
                }}
              </span>
            </div>
            <div class="club-member-balance">
              <span class="club-balance-label">
                {{ $t('posV2.clubAvailableBalance') }}
              </span>
              <span class="club-balance-value">
                {{ currency.symbol }}{{ clubMember.cashTotal }}
              </span>
            </div>
          </div>
          <p v-if="clubAmountExceedsBalance" class="club-error">
            {{ $t('posV2.clubAmountExceedsBalance') }}
          </p>
          <div v-else-if="clubPreview" class="club-preview">
            <span>
              {{
                $t('posV2.clubPayRemaining', {
                  value: currency.symbol + clubPreview.remaining,
                })
              }}
            </span>
            <span class="club-preview-separator">·</span>
            <span>
              {{
                $t('posV2.clubPayBalanceAfter', {
                  value: currency.symbol + clubPreview.balanceAfter,
                })
              }}
            </span>
          </div>
          <v-btn text color="primary" :disabled="paying" @click="resetClubState">
            {{ $t('posV2.clubUseAnotherCode') }}
          </v-btn>
        </template>
        <template v-else>
          <h3>{{ $t('posV2.clubMemberCode') }}</h3>
          <p v-if="clubCodeHintText" class="club-expired-hint">
            {{ clubCodeHintText }}
          </p>
          <v-text-field
            :value="clubCodeInput"
            outlined
            :label="$t('posV2.clubCodePlaceholder')"
            inputmode="numeric"
            maxlength="6"
            hide-details
            :disabled="paying"
            :loading="clubResolving"
            @input="onClubCodeInput"
          />
          <p v-if="clubCodeError" class="club-error">{{ clubCodeError }}</p>
          <p v-else-if="!clubCodeHintText" class="club-hint">
            {{ $t('posV2.clubCodeHint') }}
          </p>
        </template>
      </div>
    </template>
    <template v-if="paymentData.method === PaymentMethod.CustomerAccount">
      <v-divider />
      <div class="customer-container">
        <h3>Seleccione cliente</h3>
        <div class="tw-flex tw-space-x-2">
          <v-text-field
            :value="selectedCustomer ? selectedCustomer.name : ''"
            outlined
            label="Cliente"
            readonly
            hide-details
            :disabled="paying"
          />
          <v-btn
            @click="openCustomerSelector"
            color="primary"
            x-large
            :disabled="paying"
          >
            Seleccionar
          </v-btn>
        </div>
      </div>
    </template>
    <v-divider />
    <div class="comment-container">
      <h3>Comentario (opcional)</h3>
      <v-text-field
        v-model="paymentData.comment"
        outlined
        label="Comentario"
        :disabled="paying"
      />
    </div>
    <v-divider />
    <div class="actions-container">
      <v-btn color="gray" x-large :disabled="paying" @click="emit('close')">
        Cerrar
      </v-btn>
      <v-btn
        class="pay-btn"
        color="primary"
        x-large
        :loading="paying"
        :disabled="!canPay"
        @click="doPayment"
      >
        Pagar {{ currency.symbol }}{{ paymentData.amount || 0 }}
      </v-btn>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .order-pay-modal {
    @apply tw-p-4 tw-space-y-4;

    .amount-container {
      @apply tw-space-y-2;
    }

    .payment-method-container {
      @apply tw-space-y-2;

      &.disabled {
        @apply tw-opacity-50 tw-pointer-events-none;
      }

      .payment-methods {
        @apply tw-grid tw-grid-cols-4 tw-gap-2;

        .payment-method {
          @apply tw-p-4 tw-border tw-border-gray-300 tw-rounded-lg tw-cursor-pointer tw-text-center tw-flex tw-items-center tw-justify-center tw-h-16;

          &:hover {
            @apply tw-bg-gray-100;
          }

          &.selected {
            @apply tw-bg-orange-500 tw-border-orange-500 tw-text-white;
          }
        }
      }
    }

    .customer-container {
      @apply tw-space-y-2;
    }

    .club-container {
      @apply tw-space-y-2;

      .club-member-card {
        @apply tw-flex tw-items-center tw-justify-between tw-gap-2 tw-rounded-lg tw-border tw-bg-gray-50 tw-p-3;

        .club-member-info {
          @apply tw-flex tw-flex-col;

          .club-member-name {
            @apply tw-font-bold;
          }

          .club-member-number {
            @apply tw-text-xs tw-text-gray-500;
          }
        }

        .club-member-balance {
          @apply tw-flex tw-flex-col tw-items-end;

          .club-balance-label {
            @apply tw-text-xs tw-text-gray-500;
          }

          .club-balance-value {
            @apply tw-text-lg tw-font-bold tw-text-green-700;
          }
        }
      }

      .club-error {
        @apply tw-mb-0 tw-text-sm tw-font-medium tw-text-red-600;
      }

      .club-hint {
        @apply tw-mb-0 tw-text-sm tw-text-gray-500;
      }

      .club-preview {
        @apply tw-flex tw-flex-wrap tw-items-center tw-gap-1 tw-rounded-lg tw-border tw-border-emerald-200 tw-bg-emerald-50 tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-tabular-nums tw-text-emerald-800;

        .club-preview-separator {
          @apply tw-text-emerald-400;
        }
      }

      .club-expired-hint {
        @apply tw-mb-0 tw-rounded-lg tw-border tw-border-amber-200 tw-bg-amber-50 tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-amber-800;
      }
    }

    .actions-container {
      @apply tw-flex tw-space-x-2;

      .pay-btn {
        @apply tw-flex-1;
      }
    }
  }
</style>
